// ═══════════════════════════════════════════════
// CashMyProperty SaaS — Frontend API Client
// ═══════════════════════════════════════════════

const API_BASE = import.meta.env.VITE_PUBLIC_API_URL || '';

interface ApiResponse<T = unknown> {
  success: boolean;
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;
  data?: T;
  pagination?: Pagination;
}

interface Pagination {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

let authToken: string | null = localStorage.getItem('saas_token');
let tenantId: number | null = localStorage.getItem('saas_tenant_id') ? parseInt(localStorage.getItem('saas_tenant_id')!, 10) : null;
let userRole: string | null = localStorage.getItem('saas_role');
let userId: number | null = localStorage.getItem('saas_user_id') ? parseInt(localStorage.getItem('saas_user_id')!, 10) : null;

export const auth = {
  getToken: () => authToken,
  setToken: (token: string) => { authToken = token; localStorage.setItem('saas_token', token); },
  clearToken: () => { authToken = null; localStorage.removeItem('saas_token'); },
  getTenantId: () => tenantId,
  setTenantId: (id: number) => { tenantId = id; localStorage.setItem('saas_tenant_id', String(id)); },
  clearTenantId: () => { tenantId = null; localStorage.removeItem('saas_tenant_id'); },
  getRole: () => userRole,
  setRole: (role: string) => { userRole = role; localStorage.setItem('saas_role', role); },
  clearRole: () => { userRole = null; localStorage.removeItem('saas_role'); },
  getUserId: () => userId,
  setUserId: (id: number) => { userId = id; localStorage.setItem('saas_user_id', String(id)); },
  clearUserId: () => { userId = null; localStorage.removeItem('saas_user_id'); },
  isAuthenticated: () => !!authToken,
  logout: () => { auth.clearToken(); auth.clearTenantId(); auth.clearRole(); auth.clearUserId(); },
};

async function request<T = unknown>(
  endpoint: string,
  options: Record<string, unknown> = {}
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  if (tenantId) {
    headers['X-Tenant-ID'] = String(tenantId);
  }

  const fetchOptions = {
    method: (options.method as string) || 'GET',
    headers,
    ...(options.body ? { body: options.body as string } : {}),
  };

  const res = await fetch(`${API_BASE}${endpoint}`, fetchOptions);

  // Handle binary responses (exports)
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    if (!res.ok) throw new Error(`Request failed: ${res.status}`);
    return { success: true, data: await res.blob() as unknown as T };
  }

  const json: ApiResponse<T> = await res.json();

  if (!res.ok || !json.success) {
    throw new Error(json.error || json.message || `Request failed: ${res.status}`);
  }

  return json;
}

// ── Auth API ─────────────────────────────────────
export const authApi = {
  login: (email: string, password: string, tid?: number) =>
    request<{ token: string; user: Record<string, unknown>; tenant_id: number }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, tenant_id: tid ?? 1 }),
    }),

  me: () =>
    request<Record<string, unknown>>('/api/auth/me'),

  /** Revoke the current session on the server. */
  logout: () =>
    request('/api/auth/logout', { method: 'POST' }),

  /** Revoke every session for the current user. */
  logoutAll: () =>
    request('/api/auth/logout-all', { method: 'POST' }),
};

// ── Health API ────────────────────────────────────
export const healthApi = {
  check: () => request<{ status: string; database: string; uptime: number }>('/api/health'),
};

// ── Tenants API ───────────────────────────────────
export const tenantsApi = {
  list: (page = 1, perPage = 20) =>
    request<unknown[]>('/api/tenants?page=' + page + '&per_page=' + perPage),

  get: (id: number) =>
    request<Record<string, unknown>>('/api/tenants/' + id),

  create: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/tenants/' + id, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    request('/api/tenants/' + id, { method: 'DELETE' }),

  /** Get tenant settings (general, features, social, seo) */
  getSettings: () =>
    request<Record<string, unknown>>('/api/tenants/settings'),

  /** Save tenant settings — supports partial: { general, features, social, seo } */
  updateSettings: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/tenants/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

// ── Listings API ──────────────────────────────────
export const listingsApi = {
  list: (params: Record<string, string | number> = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, String(v)); });
    return request<unknown[]>('/api/listings?' + qs.toString());
  },

  get: (id: number) =>
    request<Record<string, unknown>>('/api/listings/' + id),

  create: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/listings', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /** Create listing with image uploads via multipart/form-data */
  createWithImages: async (formData: FormData) => {
    const headers: Record<string, string> = {};
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    if (tenantId) headers['X-Tenant-ID'] = String(tenantId);

    const res = await fetch(`${API_BASE}/api/listings/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    const json = await res.json();
    if (!res.ok || !json.success) {
      throw new Error(json.error || json.message || `Upload failed: ${res.status}`);
    }
    return json;
  },

  /** Update listing with optional image add/remove via multipart/form-data */
  updateWithImages: async (id: number, formData: FormData) => {
    const headers: Record<string, string> = {};
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    if (tenantId) headers['X-Tenant-ID'] = String(tenantId);

    const res = await fetch(`${API_BASE}/api/listings/${id}/images`, {
      method: 'POST',
      headers,
      body: formData,
    });

    const json = await res.json();
    if (!res.ok || !json.success) {
      throw new Error(json.error || json.message || `Image update failed: ${res.status}`);
    }
    return json;
  },

  update: (id: number, data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/listings/' + id, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    request('/api/listings/' + id, { method: 'DELETE' }),

  stats: () =>
    request<Record<string, unknown>>('/api/listings/stats'),

  export: async (format: 'pdf' | 'excel' | 'csv', params: Record<string, string> = {}) => {
    const qs = new URLSearchParams(params);
    const headers: Record<string, string> = {};
    if (authToken) headers['Authorization'] = 'Bearer ' + authToken;
    if (tenantId) headers['X-Tenant-ID'] = String(tenantId);

    const res = await fetch(API_BASE + '/api/export/listings/' + format + '?' + qs.toString(), { headers });
    if (!res.ok) throw new Error('Export failed');
    return res.blob();
  },

  /** Approve or reject a property submission */
  updateApproval: (id: number, approval_status: 'approved' | 'rejected' | 'pending', status?: string) =>
    request<Record<string, unknown>>(`/api/listings/${id}/approval`, {
      method: 'PATCH',
      body: JSON.stringify({ approval_status, status }),
    }),

  /** Toggle owner info visibility */
  updateOwnerVisibility: (id: number, show: boolean) =>
    request<Record<string, unknown>>(`/api/listings/${id}/owner-visibility`, {
      method: 'PATCH',
      body: JSON.stringify({ show_owner_info: show }),
    }),

  /** Download AI analysis report PDF for a single property */
  downloadReport: async (id: number) => {
    const headers: Record<string, string> = {};
    if (authToken) headers['Authorization'] = 'Bearer ' + authToken;
    if (tenantId) headers['X-Tenant-ID'] = String(tenantId);

    const res = await fetch(`${API_BASE}/api/listings/${id}/report`, { headers });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Report generation failed' }));
      throw new Error(err.error || err.message || 'Report generation failed');
    }
    return { blob: await res.blob(), filename: `ai-report-property-${id}.pdf` };
  },

  /** Admin-only property audit trail. Pass propertyId to filter to one listing. */
  getAudit: (propertyId?: number) => {
    const qs = propertyId ? `?propertyId=${propertyId}` : '';
    return request<Record<string, unknown>[]>(`/api/listings/audit${qs}`);
  },
};

// ── Inquiries API ─────────────────────────────────
export const inquiriesApi = {
  list: (params: Record<string, string | number> = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, String(v)); });
    return request<unknown[]>('/api/inquiries?' + qs.toString());
  },

  get: (id: number) =>
    request<Record<string, unknown>>('/api/inquiries/' + id),

  updateStatus: (id: number, status: string, notes?: string) =>
    request<Record<string, unknown>>('/api/inquiries/' + id + '/status', {
      method: 'PATCH',
      body: JSON.stringify({ status, notes }),
    }),

  delete: (id: number) =>
    request('/api/inquiries/' + id, { method: 'DELETE' }),
};

// ── Reports API ──────────────────────────────────
export const reportsApi = {
  list: (params: Record<string, string | number> = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, String(v)); });
    return request<unknown[]>('/api/reports?' + qs.toString());
  },

  get: (id: number) =>
    request<Record<string, unknown>>('/api/reports/' + id),

  delete: (id: number) =>
    request('/api/reports/' + id, { method: 'DELETE' }),
};

// ── Member API ────────────────────────────────────
export const membersApi = {
  me: () =>
    request<Record<string, unknown>>('/api/members/me'),

  stats: () =>
    request<Record<string, unknown>>('/api/members/stats'),

  inquiries: (params: Record<string, string | number> = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, String(v)); });
    return request<unknown[]>('/api/members/inquiries?' + qs.toString());
  },

  reports: (params: Record<string, string | number> = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, String(v)); });
    return request<unknown[]>('/api/members/reports?' + qs.toString());
  },
};

// ── Users API ─────────────────────────────────────
export const usersApi = {
  list: (params: Record<string, string | number> = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, String(v)); });
    return request<{ data: unknown[]; pagination: Record<string, number> }>('/api/users?' + qs.toString());
  },

  get: (id: number) =>
    request<Record<string, unknown>>('/api/users/' + id),

  create: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/users/' + id, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    request('/api/users/' + id, { method: 'DELETE' }),
};

// ── Generic Export API ────────────────────────────
export const exportApi = {
  /**
   * Download an export of any data type.
   * @param type   'listings' | 'leads' | 'users' | 'deals'
   * @param format 'csv' | 'excel' | 'pdf'
   */
  download: async (type: string, format: 'csv' | 'excel' | 'pdf' = 'csv') => {
    const headers: Record<string, string> = {};
    if (authToken) headers['Authorization'] = 'Bearer ' + authToken;
    if (tenantId) headers['X-Tenant-ID'] = String(tenantId);

    const res = await fetch(`${API_BASE}/api/export/${type}/${format}`, { headers });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Export failed' }));
      throw new Error(err.error || 'Export failed');
    }
    return res.blob();
  },
};

// ── Download helper ───────────────────────────────
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Analytics API ────────────────────────────────
export const analyticsApi = {
  /** Dashboard analytics summary: listing views, inquiry trends, revenue stats */
  summary: () =>
    request<Record<string, unknown>>('/api/analytics/summary'),
};

// ── Cities API ────────────────────────────────────
export const citiesApi = {
  list: (params: Record<string, string | number> = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, String(v)); });
    return request<unknown[]>('/api/cities?' + qs.toString());
  },

  get: (id: number) =>
    request<Record<string, unknown>>('/api/cities/' + id),

  create: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/cities', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/cities/' + id, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    request('/api/cities/' + id, { method: 'DELETE' }),

  syncListingCount: (cityName: string) =>
    request<Record<string, unknown>>('/api/cities/sync', {
      method: 'POST',
      body: JSON.stringify({ city_name: cityName }),
    }),
};

// ── Content API ────────────────────────────────────
export const contentApi = {
  list: (pageFilter?: string) => {
    const qs = pageFilter ? `?page_filter=${pageFilter}` : '';
    return request<Record<string, unknown>>('/api/content' + qs);
  },

  get: (page: string, section: string) =>
    request<Record<string, unknown>>(`/api/content/${page}/${section}`),

  /** Create or update a single content block */
  save: (page: string, section: string, data: unknown) =>
    request<Record<string, unknown>>('/api/content', {
      method: 'POST',
      body: JSON.stringify({ page, section, data }),
    }),

  update: (page: string, section: string, data: unknown) =>
    request<Record<string, unknown>>(`/api/content/${page}/${section}`, {
      method: 'PUT',
      body: JSON.stringify({ data }),
    }),

  delete: (page: string, section: string) =>
    request(`/api/content/${page}/${section}`, { method: 'DELETE' }),

  /** Bulk update all sections of a page */
  bulkUpdate: (page: string, sections: Record<string, unknown>) =>
    request<unknown[]>('/api/content/bulk', {
      method: 'POST',
      body: JSON.stringify({ page, sections }),
    }),
};

// ── Leads API ─────────────────────────────────────
export const leadsApi = {
  list: (params: Record<string, string | number> = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, String(v)); });
    return request<unknown[]>('/api/leads?' + qs.toString());
  },

  stats: () =>
    request<Record<string, unknown>>('/api/leads/stats'),

  get: (id: number) =>
    request<Record<string, unknown>>('/api/leads/' + id),

  create: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/leads', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/leads/' + id, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  updateStage: (id: number, stage: string, notes?: string) =>
    request<Record<string, unknown>>('/api/leads/' + id + '/stage', {
      method: 'PATCH',
      body: JSON.stringify({ stage, notes }),
    }),

  updateScore: (id: number, score: number) =>
    request<Record<string, unknown>>('/api/leads/' + id + '/score', {
      method: 'PATCH',
      body: JSON.stringify({ score }),
    }),

  delete: (id: number) =>
    request('/api/leads/' + id, { method: 'DELETE' }),
};

// ── Call Logs API ─────────────────────────────────
export const callLogsApi = {
  list: (params: Record<string, string | number> = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, String(v)); });
    return request<unknown[]>('/api/calls?' + qs.toString());
  },

  get: (id: number) =>
    request<Record<string, unknown>>('/api/calls/' + id),

  create: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/calls', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/calls/' + id, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    request('/api/calls/' + id, { method: 'DELETE' }),
};

// ── Reminders API ─────────────────────────────────
export const remindersApi = {
  list: (params: Record<string, string | number> = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, String(v)); });
    return request<unknown[]>('/api/reminders?' + qs.toString());
  },

  stats: () =>
    request<Record<string, unknown>>('/api/reminders/stats'),

  get: (id: number) =>
    request<Record<string, unknown>>('/api/reminders/' + id),

  create: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/reminders', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/reminders/' + id, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    request('/api/reminders/' + id, { method: 'DELETE' }),
};

// ── Deals API ─────────────────────────────────────
export const dealsApi = {
  list: (params: Record<string, string | number> = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, String(v)); });
    return request<unknown[]>('/api/deals?' + qs.toString());
  },

  stats: () =>
    request<Record<string, unknown>>('/api/deals/stats'),

  pipeline: () =>
    request<unknown[]>('/api/deals/pipeline'),

  get: (id: number) =>
    request<Record<string, unknown>>('/api/deals/' + id),

  create: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/deals', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/deals/' + id, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  updateStage: (id: number, stage: string, probability?: number) =>
    request<Record<string, unknown>>('/api/deals/' + id + '/stage', {
      method: 'PATCH',
      body: JSON.stringify({ stage, probability }),
    }),

  delete: (id: number) =>
    request('/api/deals/' + id, { method: 'DELETE' }),
};

// ── Custom Roles API ──────────────────────────────
export const customRolesApi = {
  list: (params: Record<string, string | number> = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, String(v)); });
    return request<unknown[]>('/api/custom-roles?' + qs.toString());
  },

  get: (id: number) =>
    request<Record<string, unknown>>('/api/custom-roles/' + id),

  create: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/custom-roles', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/custom-roles/' + id, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    request('/api/custom-roles/' + id, { method: 'DELETE' }),
};

// ── Public Cities API (no auth) ───────────────────
export const publicCitiesApi = {
  list: (params: Record<string, string | number> = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.set(k, String(v)); });
    // Cities endpoint is shared — falls back to public request without auth
    return fetch(`${API_BASE}/api/cities?${qs.toString()}`)
      .then(r => r.json());
  },

  suggest: (query: string, state?: string, limit = 10) => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (state) params.set('state', state);
    params.set('limit', String(limit));
    return fetch(`${API_BASE}/api/cities/suggest?${params.toString()}`)
      .then(r => r.json())
      .then((json: ApiResponse<{ name: string; state: string }[]>) => {
        if (!json.success) throw new Error(json.error || json.message || 'City suggest failed');
        return (json.data ?? []) as { name: string; state: string }[];
      });
  },
};

// ── Media / Upload API ──────────────────────────
export const mediaApi = {
  /** Upload files via multipart/form-data, returns { files: string[] } */
  upload: async (formData: FormData) => {
    const headers: Record<string, string> = {};
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    if (tenantId) headers['X-Tenant-ID'] = String(tenantId);

    const res = await fetch(`${API_BASE}/api/media/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    const json = await res.json();
    if (!res.ok || !json.success) {
      throw new Error(json.error || json.message || `Upload failed: ${res.status}`);
    }
    // Backend wraps the payload as { success, message, data: { files } }
    const payload = (json.data ?? { files: [] }) as { files: string[]; message?: string };
    return payload;
  },
};

// ── Security & Integrity API (Admin Only) ─────────
export const securityApi = {
  /** Run file integrity check against the saved baseline */
  integrityCheck: () =>
    request<Record<string, unknown>>('/api/admin/integrity'),

  /** Recreate integrity baseline (after a verified clean deploy) */
  createBaseline: () =>
    request<{ fileCount: number; message: string }>('/api/admin/integrity/baseline', { method: 'POST' }),

  /** Run a security scan now and return results */
  runScan: () =>
    request<{ scannedAt: string; alertsFound: number; alerts: unknown[] }>('/api/admin/security/scan', { method: 'POST' }),

  /** Get unresolved security alerts (newest first) */
  alerts: () =>
    request<Record<string, unknown>[]>('/api/admin/security/alerts'),

  /** Resolve a single security alert */
  resolveAlert: (id: number) =>
    request('/api/admin/security/alerts/' + id + '/resolve', { method: 'PATCH' }),

  /** Security alert summary stats */
  stats: () =>
    request<{ total: number; unresolved: number; byCategory: Array<{ category: string; count: number }> }>('/api/admin/security/stats'),
};